"use client";
import React, { useRef, useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import blogData from "../../../../data/blogPosts.json";
import Image from "next/image";
import Link from "next/link";
import blog1Data from "../../../../data/blogData/blog1.json";

// LoadingSpinner Component
const LoadingSpinner = () => (
  <motion.div
    className="flex items-center justify-center py-8 col-span-full"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    <div className="relative">
      <div className="w-16 h-16 border-4 border-[#FFAE00]/20 rounded-full"></div>
      <div className="w-16 h-16 border-4 border-[#FFAE00] border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
    </div>
  </motion.div>
);

// BlogCard Component
const BlogCard = ({ post, index }) => (
  <motion.div
    className="w-full max-w-[360px] mx-auto"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{
      duration: 0.6,
      ease: "easeOut",
      delay: index * 0.1,
    }}
  >
    <div className="flex flex-col bg-white rounded-xl overflow-hidden border border-gray-200 shadow-lg transition-all duration-300 hover:shadow-[0_12px_40px_rgba(255,174,0,0.4)] hover:scale-[1.03] cursor-pointer group h-full">
      {/* Image Section */}
      <div className="relative w-full aspect-[4/3] overflow-hidden">
        <Image
          src={post.image}
          alt={post.alt}
          fill
          className="object-cover transition-transform duration-500 group-hover:scale-110"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          priority={index < 3}
        />

        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

        {/* Category Badge */}
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1.5 bg-[#FFAE00] text-[#0D0D0D] text-xs font-bold rounded-full shadow-md">
            {post.category}
          </span>
        </div>

        {/* Read Time */}
        <div className="absolute top-4 right-4">
          <span className="px-3 py-1.5 bg-black/80 text-white text-xs font-medium rounded-full backdrop-blur-sm">
            {post.readTime}
          </span>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-6 flex-grow flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-gray-500 font-medium">{post.date}</span>
        </div>

        <h3 className="text-xl font-bold text-[#0D0D0D] mb-3 line-clamp-2 group-hover:text-[#FFAE00] transition-colors duration-300 leading-tight">
          {post.title}
        </h3>

        <p className="text-gray-600 text-sm leading-relaxed line-clamp-3 mb-6 flex-grow">
          {post.description}
        </p>

        <Link href={`/blog/${post.slug}`}>
          <button className="w-full bg-gradient-to-r from-[#FFAE00] to-[#E69A00] hover:from-[#063258] hover:to-[#063258] hover:text-white text-[#063258] font-bold py-3 px-4 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-[#FFAE00] focus:ring-opacity-50">
            Read More
          </button>
        </Link>
      </div>
    </div>
  </motion.div>
);

// FeaturedPostsSection Component
export default function Blog() {
  const containerRef = useRef(null);
  const [visiblePosts, setVisiblePosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadedCount, setLoadedCount] = useState(0);

  const INITIAL_LOAD = 3;
  const LOAD_MORE_COUNT = 3;
  const allPosts = blogData.blogPosts;

  // Load initial posts
  useEffect(() => {
    const initialPosts = allPosts.slice(0, INITIAL_LOAD);
    setVisiblePosts(initialPosts);
    setLoadedCount(INITIAL_LOAD);
    setHasMore(INITIAL_LOAD < allPosts.length);
  }, [allPosts]);

  // Lazy load more posts
  const loadMorePosts = useCallback(async () => {
    if (loading || !hasMore) return;

    setLoading(true);

    // Simulate network delay for realistic loading experience
    await new Promise((resolve) => setTimeout(resolve, 800));

    const nextStartIndex = loadedCount;
    const nextEndIndex = Math.min(
      nextStartIndex + LOAD_MORE_COUNT,
      allPosts.length
    );
    const newPosts = allPosts.slice(nextStartIndex, nextEndIndex);

    if (newPosts.length > 0) {
      setVisiblePosts((prev) => [...prev, ...newPosts]);
      setLoadedCount(nextEndIndex);
      setHasMore(nextEndIndex < allPosts.length);
    } else {
      setHasMore(false);
    }

    setLoading(false);
  }, [loading, hasMore, loadedCount, allPosts]);

  // Handle scroll detection for lazy loading
  const handleScroll = useCallback(() => {
    if (loading || !hasMore) return;

    const scrollPosition = window.scrollY + window.innerHeight;
    const pageHeight = document.documentElement.scrollHeight;

    // Trigger lazy loading when within 300px of the page bottom
    if (scrollPosition >= pageHeight - 800) {
      loadMorePosts();
    }
  }, [loadMorePosts, hasMore, loading]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", handleScroll, { passive: true });
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);


  return (
    <section
      ref={containerRef}
      className="py-8 px-4 sm:px-6 lg:px-8 mx-auto max-w-screen-xl"
    >
      <h2 className="text-4xl font-bricolage font-bold text-[#0D0D0D] mb-5 text-center">
        Featured Posts
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <AnimatePresence>
          {allPosts.map((post, index) => (
            <BlogCard key={post.slug} post={post} index={index} />
          ))}
        </AnimatePresence>
        {loading && <LoadingSpinner />}
      </div>
    </section>
  );
}
